// What keeps a crash from blanking the screen. A deploy renames the files the
// app loads part by part (a panel, the chat), so a page opened before it asks
// for files that are gone: it loads the new version, once, instead of falling
// over. Anything else that throws while drawing shows a card with a Reload
// button where the board was, never an empty screen.

import { Component, type ReactNode } from "react";

const RELOADED = "cb:reloaded-at";

/** Loads the page again for a newer version of the app, at most once in half a minute, so it can never loop. */
export function reloadForNewVersion(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOADED) || 0);
    if (Date.now() - last < 30_000) return false;
    sessionStorage.setItem(RELOADED, String(Date.now()));
  } catch {
    /* no session storage: reload anyway, once, as nothing else would help */
  }
  location.reload();
  return true;
}

/** A part of the app that could not be fetched: the version it belongs to is gone. */
export const isStaleChunk = (e: unknown) => /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Failed to fetch|MIME type|ChunkLoadError/i.test(String((e as Error)?.message ?? e));

/** Whether the board serves a newer version than this page runs: its page names another main script. */
async function newerVersionOut(): Promise<boolean> {
  try {
    const mine = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')?.getAttribute("src");
    const html = await fetch("/", { cache: "no-store" }).then((r) => r.text());
    const theirs = /<script[^>]+type="module"[^>]+src="([^"]*\/assets\/index-[^"]+)"/.exec(html)?.[1];
    return !!mine && !!theirs && mine !== theirs;
  } catch {
    return false;
  }
}

let checking = false;
/** A part of the app would not load: when that is because a new version is out, the page loads it; a passing network fault reloads nothing, and the Reload card says what to do. */
export function reloadIfNewVersion() {
  if (checking) return;
  checking = true;
  void newerVersionOut().then((yes) => {
    checking = false;
    if (yes) reloadForNewVersion();
  });
}

/** Vite says so when a part of the app it preloads cannot be fetched. */
export function watchForNewVersion() {
  window.addEventListener("vite:preloadError", () => reloadIfNewVersion());
}

export class Recover extends Component<{ children: ReactNode }, { failed: Error | null }> {
  state: { failed: Error | null } = { failed: null };
  static getDerivedStateFromError(failed: Error) {
    return { failed };
  }
  componentDidCatch(e: Error) {
    if (isStaleChunk(e)) reloadIfNewVersion();
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="recover" role="alert">
        <div className="recover-card island">
          <b>{isStaleChunk(this.state.failed) ? "The board was updated" : "Something went wrong here"}</b>
          <p>{isStaleChunk(this.state.failed) ? "Reload to open the new version. Your board is saved." : "Your board is saved. Reload to carry on."}</p>
          <button className="btn btn-primary" onClick={() => location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}
